import { SettingsName } from "@app/class/SettingsName";
import { AccountService, IAccount } from "@services/account.service";
import { NavigationService } from "@services/navigation.service";
import * as angular from "angular";
import { ConstantsService } from "./constantsService";
import { CurrentUserService } from "./currentUser.service";
import { SessionService } from "./session.service";

export interface IProcessResult {
    IsSuccess?: boolean;
    ExceptionMessage?: string;
    Value?: any;
}

export interface ILoginResponse {

    // case sensitive, MUST be all lowercase

    access_token: string;
    accountSettings: string;

    expires_in: number;
    refresh_token: string;
    token_type: string;
}

export class LoginService {
    tokenUrl: string;

    static $inject = ["$http", "$q", "sessionService", "currentUserService", "constantsService", "accountService", "navigationService"];

    constructor(
        private readonly $http: angular.IHttpService,
        private readonly $q: angular.IQService,
        private readonly sessionService: SessionService,
        private readonly currentUserService: CurrentUserService,
        private readonly constantsService: ConstantsService,
        private readonly accountService: AccountService,
        private readonly navigationService: NavigationService) {

        this.tokenUrl = this.constantsService.getTokenUrl();
    }

    sendForgotPasswordEmail(email: string): angular.IPromise<any> {
        return this
            .$http({
                url: "/api/forgottenpassword",
                method: "POST",
                data: JSON.stringify(email)
            });
    }

    loginWithToken(token: string, flushExistingSession = false): angular.IPromise<ILoginResponse> {
        return this.login("---", token, flushExistingSession);
    }

    login(username: string, password: string, flushExistingSession = false): angular.IPromise<ILoginResponse> {
        if (flushExistingSession) {
            // Flush any existing session
            this.sessionService.clearAllCachesAndSessions();
        }

        const encodedUsername = encodeURIComponent(username);

        return this.$http(
            {
                url: this.tokenUrl,
                method: "POST",
                data: `grant_type=password&username=${encodedUsername}&password=${encodeURIComponent(password)}`,
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded"
                }
            })
            .then((response: angular.IHttpResponse<ILoginResponse>) => {
                this.storeLoginSession(response.data);

                return response.data;
            });
    }

    loginSaml(username: string, flushExistingSession = false): void {
        if (flushExistingSession) {
            // Flush any existing session
            this.sessionService.clearAllCachesAndSessions();
        }

        window.location.href = `${this.tokenUrl}/sso/Login?userName=${username}`
    }

    isSSOEnabled(username: string): angular.IPromise<IProcessResult> {
        return this.$http(
            {
                url: `${this.tokenUrl}/sso/enabled`,
                method: "POST",
                data: `username=${username}`,
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded"
                }
            })
            .then((response: angular.IHttpResponse<IProcessResult>) => {
                return response.data;                
            });
    }

    switchAccount(accountId: number, reloadOnSuccess = false): angular.IPromise<ILoginResponse> {
        return this.sessionService.switchAccount(accountId)
            .finally(() => {
                this.currentUserService.getCurrentUser(true).then(() => {
                    this.accountRedirect(reloadOnSuccess);
                });
            });
    }

    switchToInternal(): angular.IPromise<ILoginResponse> {
        this.sessionService.removeItem(SettingsName.AccountUserMappings);

        return this.sessionService.switchToInternal()
            .finally(() => {
                this.currentUserService.getCurrentUser(true).then(() => {
                    this.accountRedirect(true).catch((error) => {
                        this.navigationService.reloadBrowserLocation();
                    });
                })
            });
    }

    accountRedirect(reload = false): angular.IPromise<void> {
        return this.getAllUserAndAccountSettings(true).then((accountData) => {
            this.navigationService.goToDefaultPage(accountData, reload);
        });
    }

    getAllUserAndAccountSettings(invalidateCache = true): angular.IPromise<IAccount> {
        return this.currentUserService.getCurrentUser().then(currentUser => {
            // We chain these, as we need current user BEFORE we get account data
            return this.accountService.getAccountData(invalidateCache)
                .then(accountData => {

                    return this.sessionService.getAllUserAndAccountSettings()
                        .then(() => {
                            return accountData;
                        })
                        .finally(() => {
                            return accountData;
                        });
                })
                .finally(() => {
                    this.currentUserService.performUserValidationChecks();
                });
        });
    }

    // ---

    private storeLoginSession(loginResponse: ILoginResponse) {
        if (loginResponse != null && loginResponse.access_token.length > 0) {
            this.sessionService.setStorage(loginResponse);
        }
        else {
            console.warn("Didnt store login response...");
        }
    }
}
